import { PaintCollection } from "@omni/types/api/paintColors/bff/paintCollection.model";
import { PaintColorInfo } from "@omni/types/api/paintColors/bff/shared/paintColorInfo.model";
import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { attempt } from "@omni/utils/function.utils";
import { isDefined } from "@omni/utils/helper.utils";
import queryString from "query-string";
import { createContext, use, useEffect, useMemo, useState } from "react";

import { useBFFProductServiceApi } from "@/api/hooks/productService.hook";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { mapSeries } from "@/utils/helpers";
import { useIsClientSide } from "@/utils/hooks/isClient.hook";
import { useWizard } from "@/utils/hooks/wizard/wizard.hook";
import { StepMeta } from "@/utils/hooks/wizard/wizard.types";
import { PaintConfiguration } from "@/utils/productConfiguration/visuals/paintConfiguration/paintConfigurator.types";

import { StepBaseProps } from "../components/step/step.base";
import { isPaintConfiguration } from "../helpers/config.helper";
import { getFeature } from "../helpers/feature.helper";
import {
    FeatureCodes,
    FeatureInfo,
    PaintConfiguratorWizardState,
    PaintConfiguratorWizardStepMeta,
    VolumeInfo
} from "../paintConfigurator.types";

type PaintConfiguratorContextValue = {
    selectedCollection: PaintCollection | undefined;
    collections: Array<PaintCollection>;
    selectedFamily: string | null;
    setSelectedFamily: (family: string) => void;
    selectedColor: PaintColorInfo | null;
    setSelectedColor: (color: PaintColorInfo) => void;
    selectedFinish: FeatureInfo | null;
    setSelectedFinish: (finish: string) => void;
    selectedVolume: VolumeInfo | null;
    setSelectedVolume: (volume: string) => void;
    selectedProduct: Product<ProductFieldSet.Full> | null;
    currentStep?: StepMeta<PaintConfiguratorWizardStepMeta>;
    goToStep: (step: number) => void;
    nextStep: () => void;
    previousStep: () => void;
    canGoBack: boolean;
    finishes: Map<string, FeatureInfo>;
    volumes: Map<string, VolumeInfo>;
    steps: Array<{
        component: React.FC<StepBaseProps>;
        meta: PaintConfiguratorWizardStepMeta;
    }>;
    products: Array<Product<ProductFieldSet.Full>>;
    isLoadingProducts: boolean;
    isRestoringState: boolean;
};

type PaintConfiguratorContextProviderProps = RequireChildrenFor<{
    collections: Array<PaintCollection>;
    steps: Array<{
        component: React.FC<StepBaseProps>;
        meta: PaintConfiguratorWizardStepMeta;
    }>;
}>;

export const PaintConfiguratorContext = createContext<
    PaintConfiguratorContextValue | undefined
>(undefined);
PaintConfiguratorContext.displayName = "PaintConfiguratorContext";

export const PaintConfiguratorContextProvider = ({
    collections,
    steps,
    children
}: PaintConfiguratorContextProviderProps) => {
    const { t } = useGranite();
    const isClientSide = useIsClientSide();
    const [isRestoringState, setRestoringState] = useState(false);

    const mappedSteps = (steps || []).map<
        StepMeta<PaintConfiguratorWizardStepMeta>
    >((step, index) => ({
        index,
        meta: step.meta
    }));

    const {
        state,
        currentStep,
        nextStep,
        previousStep,
        goToStep,
        setState,
        canGoBack,
        reset
    } = useWizard<
        PaintConfiguratorWizardState,
        PaintConfiguratorWizardStepMeta
    >(
        mappedSteps,
        {
            family: undefined,
            color: undefined,
            finish: undefined,
            volume: undefined,
            product: undefined
        },
        { scrollToTop: true }
    );
    const [products, setProducts] = useState<
        Array<Product<ProductFieldSet.Full>>
    >([]);

    const [{ loading }, getProductsForColor] = useBFFProductServiceApi<
        any,
        any,
        Array<Product<ProductFieldSet.Full>>
    >(
        {
            params: {
                fields: ProductFieldSet.Full
            },
            url: `/paint-colors/:colorId/products`,
            method: "GET"
        },
        { autoCancel: false, manual: true, useCache: false }
    );

    const stateGeneratorSequence = [
        (paintConfig: PaintConfiguration) =>
            Promise.resolve(onSelectFamily(paintConfig.paintInfo.family)),
        (paintConfig: PaintConfiguration) => {
            const color = selectedCollection?.families
                .find((family) => family.name === paintConfig.paintInfo.family)
                ?.colors.find(
                    (color) => color.id === paintConfig.paintInfo.colorId
                );
            return onSelectColor(color!);
        },
        (paintConfig: PaintConfiguration) =>
            Promise.resolve(onSelectFinish(paintConfig.paintInfo.finish)),
        (paintConfig: PaintConfiguration) =>
            Promise.resolve(onSelectVolume(paintConfig.paintInfo.volume))
    ];

    const restoreState = async (paintConfig: PaintConfiguration) => {
        try {
            setRestoringState(true);
            await mapSeries(stateGeneratorSequence, async (generator) => {
                await generator(paintConfig);
                nextStep();
            });
        } catch {
            reset();
        } finally {
            setRestoringState(false);
        }
    };

    const queryParams = useMemo(() => {
        if (!isClientSide) return null;

        return attempt(() => {
            const qs = queryString.parse(window.location.search);
            if (!qs) return null;

            return qs["pc"] as string;
        }, null);
    }, [isClientSide]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (!queryParams) return;

        const decodeValue = attempt(() => {
            return JSON.parse(decodeURIComponent(atob(queryParams)));
        }, null);

        if (isPaintConfiguration(decodeValue)) {
            restoreState(decodeValue);
        }
    }, [queryParams]);

    const onSelectFamily = (family: string) => {
        setState({ family });
        setProducts([]);
    };

    const onSelectColor = async (color: PaintColorInfo) => {
        setState({ color });
        try {
            const { data } = await getProductsForColor({
                urlParams: {
                    colorId: color.id
                }
            });
            setProducts(data);
        } catch {
            setProducts([]);
        }
    };

    const onSelectFinish = (finish: string) => {
        setState({ finish });
    };

    const onSelectVolume = (volume: string) => {
        setState({ volume });
    };

    const selectedCollection = collections.at(0);

    const finishes = products.reduce((acc, product) => {
        const finish = getFeature(t, product, FeatureCodes.FinishFeatureCode);
        if (isDefined(finish)) {
            acc.set(finish.value, finish);
        }
        return acc;
    }, new Map<string, FeatureInfo>());

    const volumes = products
        .filter(
            (p) =>
                getFeature(t, p, FeatureCodes.FinishFeatureCode)?.value ===
                state.finish
        )
        .reduce((acc, product) => {
            const volume = getFeature(
                t,
                product,
                FeatureCodes.VolumeFeatureCode
            );
            const coverage = getFeature(
                t,
                product,
                FeatureCodes.CoverageFeatureCode,
                t("paintConfigurator.coverage.label")
            );
            if (isDefined(volume) && isDefined(coverage)) {
                acc.set(volume.value, {
                    volume,
                    coverage
                });
            }
            return acc;
        }, new Map<string, VolumeInfo>());

    const filteredProducts = products.filter(
        (b) =>
            getFeature(t, b, FeatureCodes.FinishFeatureCode)?.value ===
                state.finish &&
            getFeature(t, b, FeatureCodes.VolumeFeatureCode)?.value ===
                state.volume
    );

    return (
        <PaintConfiguratorContext
            value={{
                collections,
                selectedCollection,
                selectedFamily: state.family || null,
                selectedColor: state.color || null,
                selectedFinish: finishes.get(state.finish ?? "") || null,
                selectedVolume: volumes.get(state.volume ?? "") || null,
                selectedProduct: state.product || null,
                setSelectedFamily: onSelectFamily,
                setSelectedColor: onSelectColor,
                setSelectedFinish: onSelectFinish,
                setSelectedVolume: onSelectVolume,
                finishes,
                volumes,
                currentStep,
                steps,
                goToStep,
                nextStep,
                previousStep,
                canGoBack,
                products: filteredProducts,
                isLoadingProducts: loading,
                isRestoringState
            }}
        >
            {children}
        </PaintConfiguratorContext>
    );
};

export const usePaintConfiguratorContext = () => {
    const context = use<PaintConfiguratorContextValue | undefined>(
        PaintConfiguratorContext
    );

    if (!isDefined(context)) {
        throw new Error(
            `${PaintConfiguratorContext.displayName} is undefined.`
        );
    }

    return context;
};
